/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import appTiles from './assets/twc-app-tiles.svg';
import { messages } from './messages';

const marginStyles = cssMap({
	none: {},
	'space.150': {
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
});

const styles = cssMap({
	root: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	appTilesWrapper: {
		marginTop: token('space.150'),
	},
});

export type TwcSampleBannerProps = {
	href: string;
	margin?: 'none' | 'space.150';
	onClose?: () => void;
	product: 'jira' | 'confluence';
	testId?: string;
};

export const TwcSampleBanner = ({
	href,
	margin = 'none',
	onClose,
	product,
	testId = 'platform-growth-twc-sample-banner',
}: TwcSampleBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={cx(styles.root, marginStyles[margin])} testId={testId}>
			<Stack spread="space-between" space="space.150">
				<Inline spread="space-between">
					<Box xcss={styles.appTilesWrapper}>
						<Image
							src={appTiles}
							srcDark={appTiles}
							alt={formatMessage(messages.appTilesAlt)}
							testId="twc-sample-banner-app-tiles"
							width={91}
						/>
					</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={onClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-sample-banner-close"
						appearance="subtle"
					/>
				</Inline>
				<Stack spread="space-between" space="space.100">
					<Heading size="small" testId="twc-sample-banner-heading">
						<FormattedMessage {...messages.heading} />
					</Heading>
					<Text size="small" color="color.text.subtle" testId="twc-sample-banner-subheading">
						{product === 'jira' ? (
							<FormattedMessage {...messages.subheadingJira} />
						) : (
							<FormattedMessage {...messages.subheadingConfluence} />
						)}
					</Text>
				</Stack>
				<Inline alignBlock="start">
					<LinkButton appearance="default" href={href} testId="twc-sample-banner-button-try-now">
						<FormattedMessage {...messages.tryNowButton} />
					</LinkButton>
				</Inline>
			</Stack>
		</Box>
	);
};
